import { useEffect, useState } from "react";
import axios from "axios";

export default function Profile({ onLoginClick }) {
    const [user, setUser] = useState(null);
    const [form, setForm] = useState({});
    const [bookings, setBookings] = useState([]);
    const [activeTab, setActiveTab] = useState("profile");
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);

    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(false);
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(new Date());
        }, 1000); // 🔥 every second

        return () => clearInterval(interval);
    }, []);



    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem("user"));

        // If user is not logged in
        if (!storedUser) {
            setUser(null);
            setBookings([]);
            return;
        }

        setUser(storedUser);

        setForm({
            name: storedUser.name || "",
            email: storedUser.email || "",
            phone: storedUser.phone || "",
            nationality: storedUser.nationality || "",
        });

        fetchBookings(storedUser.id);
    }, []);

    const fetchBookings = async (userId) => {
        try {
            const res = await axios.get(
                `https://hotel.southtravels.com/api/user/${userId}/bookings`
            );
            setBookings(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const updateProfile = async (e) => {
        e.preventDefault();

        try {
            const res = await axios.put(
                `https://hotel.southtravels.com/api/user/${user.id}`,
                form
            );

            localStorage.setItem("user", JSON.stringify(res.data.user));
            setUser(res.data.user);

            alert("Profile updated successfully");
        } catch (err) {
            console.error(err);
            alert("Update failed");
        }
    };

    const formatDate = (date) =>
        new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });

    const cancelBooking = async (id) => {
        if (!window.confirm("Are you sure you want to cancel this booking?")) return;

        try {
            await axios.post(`https://hotel.southtravels.com/api/booking/${id}/cancel`);

            alert("Booking cancelled");

            // refresh bookings
            fetchBookings(user.id);

        } catch (err) {
            alert(err.response?.data?.message || "Cancel failed");
        }
    };

    const openReviewModal = (booking) => {
        setSelectedBooking(booking);
        setShowReviewModal(true);
    };

    const submitReview = async () => {
        if (rating === 0) {
            alert("Please select a rating");
            return;
        }

        setLoading(true);

        try {
            if (selectedBooking.review) {
                // ✅ UPDATE
                await axios.put(
                    `https://hotel.southtravels.com/api/reviews/${selectedBooking.review.id}`,
                    {
                        rating,
                        comment
                    }
                );

                alert("Review updated!");
            } else {
                // ✅ CREATE
                await axios.post("https://hotel.southtravels.com/api/reviews", {
                    booking_id: selectedBooking.id,
                    hotel_id: selectedBooking.hotel_id,
                    email: user.email,
                    rating,
                    comment
                });

                alert("Review submitted!");
            }

            setShowReviewModal(false);
            setRating(0);
            setComment("");

            fetchBookings(user.id); // 🔥 refresh UI

        } catch (err) {
            alert(err.response?.data?.message || "Error");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteReview = async (reviewId) => {
        if (!window.confirm("Delete this review?")) return;

        try {
            await axios.delete(`https://hotel.southtravels.com/api/reviews/${reviewId}`);

            alert("Review deleted");

            fetchBookings(user.id); // refresh

        } catch (err) {
            alert("Delete failed");
        }
    };

    const handleEditReview = (booking) => {
        setSelectedBooking(booking);
        setRating(booking.review.rating);
        setComment(booking.review.comment);
        setShowReviewModal(true);
    };

    const normalizeDate = (date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const isPastCheckout = (date) => {
        return normalizeDate(date) < normalizeDate(new Date());
    };

    const isFutureCheckIn = (date) => {
        return normalizeDate(date) > normalizeDate(new Date());
    };

    const getCancelDeadline = (booking) => {
        const createdAt = new Date(booking.created_at);
        const checkIn = new Date(booking.check_in);

        const freeCancelUntil = new Date(createdAt.getTime() + 24 * 60 * 60 * 1000);

        return freeCancelUntil < checkIn ? freeCancelUntil : checkIn;
    };

    const canCancel = (booking) => {
        return new Date() < getCancelDeadline(booking);
    };

    const getCancelCountdown = (booking) => {
        const now = new Date();
        const deadline = getCancelDeadline(booking);

        const diff = deadline - now;

        if (diff <= 0) return null;

        const totalSeconds = Math.floor(diff / 1000);

        return {
            hours: Math.floor(totalSeconds / 3600),
            minutes: Math.floor((totalSeconds % 3600) / 60),
            seconds: totalSeconds % 60
        };
    };

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="bg-white shadow-lg rounded-2xl p-8 text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-3">
                        Please Login
                    </h2>

                    <p className="text-gray-500 mb-5">
                        You must login to view your profile and bookings.
                    </p>

                    <button
                        onClick={onLoginClick}
                        className="inline-block bg-teal-500 text-white px-6 py-3 rounded-lg hover:bg-teal-600 transition"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">

                {/* Sidebar */}
                <div className="lg:col-span-3 bg-white rounded-2xl shadow-md border border-gray-100 p-4 sm:p-5">

                    <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 sm:mb-5">
                        My Account
                    </h2>

                    {/* Profile */}
                    <button
                        onClick={() => setActiveTab("profile")}
                        className={`w-full flex items-center gap-3 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${activeTab === "profile"
                            ? "bg-teal-50 text-teal-600 border-l-4 border-teal-500"
                            : "text-gray-600 hover:bg-gray-50"
                            }`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                        </svg>
                        Profile
                    </button>

                    {/* Bookings */}
                    <button
                        onClick={() => setActiveTab("bookings")}
                        className={`w-full flex items-center gap-3 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 mt-2 ${activeTab === "bookings"
                            ? "bg-teal-50 text-teal-600 border-l-4 border-teal-500"
                            : "text-gray-600 hover:bg-gray-50"
                            }`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                d="M3 7h18M3 12h18M3 17h18"
                            />
                        </svg>
                        My Bookings
                    </button>

                </div>

                {/* Main Content */}
                <div className="lg:col-span-9">

                    {/* PROFILE TAB */}
                    {activeTab === "profile" && (
                        <div className="bg-white rounded-2xl shadow p-4 sm:p-6">
                            <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
                                Profile Information
                            </h1>

                            <form
                                onSubmit={updateProfile}
                                className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4"
                            >
                                <input
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    className="border p-2 sm:p-3 rounded-lg focus:ring-2 focus:ring-teal-500"
                                    placeholder="Full Name"
                                />

                                <input
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    className="border p-2 sm:p-3 rounded-lg focus:ring-2 focus:ring-teal-500"
                                    placeholder="Email"
                                />

                                <input
                                    name="phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    className="border p-2 sm:p-3 rounded-lg focus:ring-2 focus:ring-teal-500"
                                    placeholder="Phone"
                                />

                                <input
                                    name="nationality"
                                    value={form.nationality}
                                    onChange={handleChange}
                                    className="border p-2 sm:p-3 rounded-lg focus:ring-2 focus:ring-teal-500"
                                    placeholder="Nationality"
                                />

                                <button className="sm:col-span-2 bg-gradient-to-r from-teal-500 to-blue-500 text-white py-2 sm:py-3 rounded-lg text-sm sm:text-base font-medium hover:scale-105 transition">
                                    Update Profile
                                </button>
                            </form>
                        </div>
                    )}

                    {/* BOOKINGS TAB */}
                    {activeTab === "bookings" && (
                        <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-6 sm:p-8">
                            {/* Header */}
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                                <div>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                                        My Bookings
                                    </h1>
                                    <p className="text-sm text-gray-500 mt-1">
                                        View, manage cancellations and leave reviews.
                                    </p>
                                </div>

                                <div className="bg-slate-50 border border-slate-200 px-5 py-3 rounded-2xl">
                                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                                        Total Bookings
                                    </p>
                                    <p className="text-xl font-bold text-slate-800">
                                        {bookings.length}
                                    </p>
                                </div>
                            </div>

                            {bookings.length === 0 ? (
                                <div className="text-center py-20">
                                    <div className="text-5xl mb-4">🏨</div>
                                    <h3 className="text-lg font-semibold text-gray-800">
                                        No bookings yet
                                    </h3>
                                    <p className="text-gray-500 mt-2 text-sm">
                                        Your upcoming reservations will appear here.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {bookings.map((b) => (
                                        <div
                                            key={b.id}
                                            className="border border-gray-200 rounded-3xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-300"
                                        >
                                            {/* Top Bar */}
                                            <div className="bg-gradient-to-r from-slate-50 to-white border-b px-6 py-5 flex flex-col md:flex-row md:justify-between gap-4">
                                                <div>
                                                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                                                        Reservation
                                                    </p>

                                                    <h3 className="text-xl font-semibold text-gray-900">
                                                        {b.hotel_name}
                                                    </h3>

                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {b.room_name} • Room {b.room_number}
                                                    </p>
                                                </div>

                                                <div className="flex items-start md:items-center">
                                                    <span
                                                        className={`px-4 py-2 rounded-full text-xs font-semibold tracking-wide
                                ${b.status === "confirmed"
                                                                ? "bg-green-100 text-green-700"
                                                                : b.status === "cancelled"
                                                                    ? "bg-red-100 text-red-700"
                                                                    : "bg-yellow-100 text-yellow-700"
                                                            }`}
                                                    >
                                                        {b.status}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Booking Info */}
                                            <div className="p-6">
                                                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
                                                    <div className="bg-gray-50 rounded-2xl p-4">
                                                        <p className="text-xs text-gray-500 mb-1">
                                                            Check-in
                                                        </p>
                                                        <p className="font-semibold text-gray-800">
                                                            {formatDate(b.check_in)}
                                                        </p>
                                                    </div>

                                                    <div className="bg-gray-50 rounded-2xl p-4">
                                                        <p className="text-xs text-gray-500 mb-1">
                                                            Check-out
                                                        </p>
                                                        <p className="font-semibold text-gray-800">
                                                            {formatDate(b.check_out)}
                                                        </p>
                                                    </div>

                                                    <div className="bg-gray-50 rounded-2xl p-4">
                                                        <p className="text-xs text-gray-500 mb-1">
                                                            Guests
                                                        </p>
                                                        <p className="font-semibold text-gray-800">
                                                            {b.guests} Guests
                                                        </p>
                                                    </div>

                                                    <div className="bg-emerald-50 rounded-2xl p-4">
                                                        <p className="text-xs text-emerald-600 mb-1">
                                                            Amount Paid
                                                        </p>
                                                        <p className="font-bold text-emerald-700">
                                                            AED {b.total_amount}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Actions */}
                                                <div className="flex flex-wrap gap-3 items-center">
                                                    {b.status === "confirmed" && canCancel(b) && (
                                                        <>
                                                            <button
                                                                onClick={() => cancelBooking(b.id)}
                                                                className="px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition"
                                                            >
                                                                Cancel Booking
                                                            </button>

                                                            {(() => {
                                                                const time = getCancelCountdown(b);

                                                                if (!time) return null;

                                                                return (
                                                                    <div className="px-4 py-2 rounded-xl bg-amber-50 border border-amber-200 text-xs font-medium text-amber-700">
                                                                        Free cancellation ends in{" "}
                                                                        {time.hours
                                                                            .toString()
                                                                            .padStart(2, "0")}
                                                                        :
                                                                        {time.minutes
                                                                            .toString()
                                                                            .padStart(2, "0")}
                                                                        :
                                                                        {time.seconds
                                                                            .toString()
                                                                            .padStart(2, "0")}
                                                                    </div>
                                                                );
                                                            })()}
                                                        </>
                                                    )}

                                                    {b.status === "confirmed" &&
                                                        isPastCheckout(b.check_out) &&
                                                        !b.review && (
                                                            <button
                                                                onClick={() => openReviewModal(b)}
                                                                className="px-5 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition"
                                                            >
                                                                Leave Review
                                                            </button>
                                                        )}
                                                </div>

                                                {b.status === "confirmed" &&
                                                    !isPastCheckout(b.check_out) && (
                                                        <p className="text-sm text-gray-400 mt-4">
                                                            Reviews can be submitted after checkout.
                                                        </p>
                                                    )}

                                                {/* Review */}
                                                {b.review && (
                                                    <div className="mt-6 rounded-2xl border bg-slate-50 p-5">
                                                        <div className="flex justify-between items-center mb-3">
                                                            <h4 className="font-semibold text-gray-900">
                                                                Your Review
                                                            </h4>

                                                            <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-semibold">
                                                                ⭐ {b.review.rating}/5
                                                            </span>
                                                        </div>

                                                        <p className="text-sm text-gray-600 leading-relaxed">
                                                            {b.review.comment}
                                                        </p>

                                                        <div className="flex gap-3 mt-4 flex-wrap">
                                                            <button
                                                                onClick={() => handleEditReview(b)}
                                                                className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-700"
                                                            >
                                                                Edit Review
                                                            </button>

                                                            <button
                                                                onClick={() =>
                                                                    handleDeleteReview(b.review.id)
                                                                }
                                                                className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm hover:bg-red-700"
                                                            >
                                                                Delete Review
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {showReviewModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center px-4">
                    <div className="bg-white p-4 sm:p-6 rounded-xl w-full max-w-md">

                        <h2 className="text-lg sm:text-xl font-bold mb-4">Add Review</h2>

                        <p className="text-xs sm:text-sm text-gray-500 mb-4">
                            {selectedBooking?.hotel_name}
                        </p>

                        <div className="flex gap-2 mb-4 justify-center sm:justify-start">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <span
                                    key={star}
                                    onClick={() => setRating(star)}
                                    className={`cursor-pointer text-xl sm:text-2xl ${star <= rating ? "text-yellow-500" : "text-gray-300"}`}
                                >
                                    ★
                                </span>
                            ))}
                        </div>

                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Write your experience..."
                            className="w-full border p-2 sm:p-3 rounded-lg mb-4 focus:ring-2 focus:ring-yellow-400"
                            rows="3"
                        />

                        <button
                            onClick={submitReview}
                            disabled={loading}
                            className="w-full bg-yellow-500 text-white py-2 rounded-lg hover:bg-yellow-600"
                        >
                            {loading ? "Submitting..." : "Submit Review"}
                        </button>

                        <button
                            onClick={() => setShowReviewModal(false)}
                            className="mt-4 px-4 py-2 bg-gray-300 rounded w-full sm:w-auto"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}